import React from "react";
import classnames from "classnames";

import { BsBriefcase } from "react-icons/bs";
import { FiDollarSign, FiTrash2, FiEdit3, FiRepeat } from "react-icons/fi";

import { isEmpty, isNil } from "lodash";
import moment from "moment";
import { formatCurrency } from "utils/number";
import type { Maybe } from "types/graphqlSchema";

import Button from "components/Button";
import AddEmployerModal from "./AddEmployerModal";
import RemoveSourceModal from "./RemoveSourceModal";
import SourceActions from "./SourceActions";
import RecurringContributionModal from "./RecurringContributionModal";
import ContributionModal from "pages/dashboards/individualDashboard/dashboard/ContributionModal";
import { accountTypesToEnglishMapping } from "statics/accountTypes";

interface ContributionSourceModel {
  name?: string;
  // bank-sourced fields come from UserBank where they are Maybe (string | null)
  planId?: string;
  bankName?: Maybe<string>;
  accountSubtype?: Maybe<string>;
  accountType?: Maybe<string>;
  accountId?: Maybe<string>;
  id?: string;
  isLinkRequest?: boolean;
}

// Fields cover both the employer-built shape (Employers.tsx) and the
// bank/recurring shape (Banks.tsx spreads a RecurringContributionSchedule, so
// the extra schedule fields below are Maybe-typed).
interface ContributionInfo {
  amount?: Maybe<number>;
  end?: Maybe<string>;
  schedule?: string;
  scheduleId?: Maybe<string>;
  frequency?: Maybe<string>;
  accountId?: Maybe<string>;
  accountType?: Maybe<string>;
  anchorPayDate?: Maybe<string>;
  dayOne?: Maybe<number>;
  dayTwo?: Maybe<number>;
  nextScheduledDate?: Maybe<string>;
  sourceBankAccountId?: Maybe<string>;
  targetIraAccountId?: Maybe<string>;
}

interface Props {
  isEmployer?: boolean;
  // Paychex Flex (Perks) paycheck-split source. Purely presentational for this
  // type: the parent (PaycheckSplitCard) owns the edit/delete modals and their
  // redux wiring, so the Manage actions delegate to the callbacks below.
  isPaycheckSplit?: boolean;
  onEditPaycheckSplit?: () => void;
  onRemovePaycheckSplit?: () => void;
  isPendingVerification?: boolean;
  sourceId?: string;
  contributionSource?: ContributionSourceModel;
  contributionInfo?: ContributionInfo;
  employerLink?: object;
}

interface State {
  showContributionModal: boolean;
  showOneTimeContributionModal: boolean;
  showDeleteModal: boolean;
  showContributonActions: boolean;
  // written only via setState (toggles the RemoveSourceModal); not in initial state
  isDeleting?: boolean;
}

interface SourceActionItem {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
}

class ContributionSource extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showContributionModal: false,
      showOneTimeContributionModal: false,
      showDeleteModal: false,
      showContributonActions: false,
    };
  }

  _getTitle() {
    if (this.props.isEmployer) {
      return (
        <>
          <p className="company-name">{this.props.contributionSource!.name}</p>
          <p className="plan-id">
            Plan ID: {this.props.contributionSource!.planId}
          </p>
        </>
      );
    }

    if (this.props.isPaycheckSplit) {
      return (
        <p className="company-name">{this.props.contributionSource!.name}</p>
      );
    }

    return (
      <div>
        <p className="bank-name">{this.props.contributionSource!.bankName}</p>
      </div>
    );
  }

  _getSourceFooter() {
    if (this.props.isEmployer || this.props.isPaycheckSplit) {
      return <BsBriefcase size={"22px"} stroke={"0.5"} />;
    }

    const { accountId } = this.props.contributionSource!;

    return (
      <>
        <div className="bank-design">
          <div className="card-chip">
            <p className="account-type">
              {this.props.contributionSource!.accountSubtype}
              <span style={{ display: "block" }} className="bank-number">
                **** **** {accountId}
              </span>
            </p>
          </div>
        </div>
      </>
    );
  }

  _getActionMenuActions = (): SourceActionItem[] => {
    if (this.props.isPaycheckSplit) {
      return [
        {
          label: "Edit Paycheck Contribution",
          onClick: () => this.props.onEditPaycheckSplit?.(),
          icon: <FiEdit3 size="16px" color="#0a2540" />,
        },
        {
          label: "Remove Contribution",
          onClick: () => this.props.onRemovePaycheckSplit?.(),
          icon: (
            <FiTrash2
              size="16px"
              color="#FFFFFF"
              fill={"#FFFFFF"}
              stroke={"#B12121"}
              strokeWidth={"1.5"}
            />
          ),
        },
      ];
    }

    if (this.props.isEmployer) {
      return [
        {
          label: "Edit Payroll Contribution",
          onClick: () => this.setState({ showContributionModal: true }),
          icon: <FiEdit3 size="16px" color="#0a2540" />,
        },
        {
          label: "Remove Employer",
          onClick: () => this.setState({ isDeleting: true }),
          icon: (
            <FiTrash2
              size="16px"
              color="#FFFFFF"
              fill={"#FFFFFF"}
              stroke={"#B12121"}
              strokeWidth={"1.5"}
            />
          ),
        },
      ];
    }

    return [
      {
        label: "Edit Scheduled Contribution",
        onClick: () => this.setState({ showContributionModal: true }),
        icon: <FiEdit3 size="16px" color="#0a2540" />,
      },
      {
        label: "Make one-time contribution",
        onClick: () => this.setState({ showOneTimeContributionModal: true }),
        icon: <FiDollarSign size="16px" color="#0a2540" />,
      },
      {
        label: "Remove Bank",
        onClick: () => this.setState({ isDeleting: true }),
        icon: (
          <FiTrash2
            size="16px"
            color="#FFFFFF"
            fill={"#FFFFFF"}
            stroke={"#B12121"}
            strokeWidth={"1.5"}
          />
        ),
      },
    ];
  };

  _getContributionSourceActions = (): SourceActionItem[] | undefined => {
    if (isEmpty(this.props.contributionInfo)) {
      if (this.props.contributionSource!.isLinkRequest) {
        return [
          {
            label: "Setup Contribution",
            onClick: () => this.setState({ showContributionModal: true }),
            icon: <FiRepeat size="16px" color="#0a2540" />,
          },
          {
            label: "Remove Employer",
            onClick: () => this.setState({ isDeleting: true }),
            icon: <FiTrash2 size="16px" color="#0a2540" />,
          },
        ];
      }
      return [
        {
          label: "Make one-time contribution",
          onClick: () => this.setState({ showOneTimeContributionModal: true }),
          icon: <FiDollarSign size="16px" color="#0a2540" />,
        },
        {
          label: "Setup Recurring Contributions",
          onClick: () => this.setState({ showContributionModal: true }),
          icon: <FiRepeat size="16px" color="#0a2540" />,
        },
        {
          label: "Remove Bank",
          onClick: () => this.setState({ isDeleting: true }),
          icon: (
            <FiTrash2
              size="16px"
              color="#FFFFFF"
              fill={"#FFFFFF"}
              stroke={"#B12121"}
              strokeWidth={"1.5"}
            />
          ),
        },
      ];
    }
  };

  _getContributionSection() {
    if (isEmpty(this.props.contributionInfo)) {
      if (this.props.isPaycheckSplit) {
        return (
          <div className="no-contribution">
            <div className="no-contribution-content">
              <p className="no-contribution-message">Add a contribution</p>
              <p className="no-contribution-subtext">
                Automatically set aside a portion of each{" "}
                {this.props.contributionSource!.name} paycheck for your
                retirement.
              </p>
              <span className="add-contrbution-actions">
                <SourceActions
                  actions={[
                    {
                      label: "Setup Contribution",
                      onClick: () => this.props.onEditPaycheckSplit?.(),
                      icon: <FiRepeat size="16px" color="#0a2540" />,
                    },
                  ]}
                  menuTitle={"CONTRIBUTION ACTIONS"}
                />
              </span>
            </div>
          </div>
        );
      }
      if (this.props.isPendingVerification) {
        return (
          <div className="pending-bank">
            <p className="pending-bank-message">Verifying Account </p>
            <p className="no-contribution-subtext">
              Bank account is pending verification, once verified you can setup
              a recurring contribution.
            </p>

            <span className="remove-bank">
              <Button
                btnClass="actions-menu-btn"
                size="sm"
                color="red"
                name="action"
                onClick={() => this.setState({ isDeleting: true })}
              >
                Remove Bank
              </Button>
            </span>
          </div>
        );
      }
      if (this.props.contributionSource!.isLinkRequest) {
        return (
          <div className="no-contribution">
            <div className="no-contribution-content">
              <p className="no-contribution-message">Finish Setup</p>
              <p className="no-contribution-subtext">
                Finish setting up your employer account by adding your
                contribution amount.
              </p>
              <span className="add-contrbution-actions">
                <SourceActions
                  actions={this._getContributionSourceActions()}
                  menuTitle={"CONTRIBUTION ACTIONS"}
                />
              </span>
            </div>
          </div>
        );
      }
      return (
        <div className="no-contribution">
          <div className="no-contribution-content">
            <p className="no-contribution-message">Add a contribution</p>
            <p className="no-contribution-subtext">
              With a linked bank account you can set up recurring and or make a
              one-time contribution.
            </p>
            <span className="add-contrbution-actions">
              <SourceActions
                actions={this._getContributionSourceActions()}
                menuTitle={"CONTRIBUTION ACTIONS"}
              />
            </span>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="contribution-heading">
          <h3 className="contribution">Contribution</h3>
          <div className="contribution-schedule">
            <span>
              <FiRepeat
                size={12}
                color={"#ffffff"}
                fill={"#ffffff"}
                stroke={"#6b7c93"}
                strokeWidth={"1.5"}
              />
            </span>
            <span className="schedule">
              {this.props.contributionInfo!.schedule}
            </span>
          </div>
        </div>
        <div className="contribution-amount-and-actions">
          <span className="contribution-amount">
            {formatCurrency(this.props.contributionInfo!.amount!)}
          </span>
        </div>
        <div className="flex-break" />
        <div className="info-wrapper">
          <div className="info-group">
            {!isNil(this.props.contributionInfo) && (
              <div className="info">
                <span className="dot" />
                <span>
                  {`${
                    this.props.isEmployer || this.props.isPaycheckSplit
                      ? "Payroll"
                      : "Recurring"
                  } Contribution Account: ${
                    accountTypesToEnglishMapping[
                      this.props.contributionInfo!
                        .accountType as keyof typeof accountTypesToEnglishMapping
                    ]
                  }`}
                </span>
              </div>
            )}
            {/* Worker records don't expose a next payroll date (v1). */}
            {!this.props.isPaycheckSplit && (
              <div className="info">
                <span className="dot" />
                <span>
                  Next{" "}
                  {this.props.isEmployer
                    ? "payroll date: "
                    : "contribution date: "}
                  {moment(this.props.contributionInfo!.end).format(
                    "MMMM Do, YYYY"
                  )}
                </span>
              </div>
            )}
          </div>
          <div className="menu-btn">
            <SourceActions
              actions={this._getActionMenuActions()}
              menuTitle={
                this.props.isEmployer
                  ? "Employer"
                  : this.props.isPaycheckSplit
                    ? "Paycheck"
                    : "Bank Account"
              }
            />
          </div>
        </div>
      </>
    );
  }

  render() {
    return (
      <div
        className={classnames("contribution-source-container", {
          "bank-source": !this.props.isEmployer && !this.props.isPaycheckSplit,
          // Paycheck-split sources share the employer look (dark left panel).
          "employer-source":
            this.props.isEmployer || this.props.isPaycheckSplit,
        })}
      >
        {this.state.showContributionModal && this.props.isEmployer && (
          <AddEmployerModal
            show={this.state.showContributionModal}
            employerLink={this.props.employerLink}
            isLinkRequest={this.props.contributionSource!.isLinkRequest}
            isEditing={true}
            onClose={() => this.setState({ showContributionModal: false })}
          />
        )}

        {this.state.showContributionModal &&
          !this.props.isEmployer &&
          !this.props.isPaycheckSplit && (
            <RecurringContributionModal
              show={true}
              // contributionInfo/contributionSource fields are Maybe (string|null
              // | number|null); the modal's props are the non-null variants. The
              // original JS passed these raw (null included), so cast rather than
              // coalesce to preserve the exact runtime value.
              scheduleId={
                this.props.contributionInfo!.scheduleId as string | undefined
              }
              bankId={this.props.contributionSource!.id}
              onClose={() => this.setState({ showContributionModal: false })}
              // Dead pass: RecurringContributionModal injects recurringContribution
              // from its own mapStateToProps, so connect drops this parent-passed
              // key from the accepted props. Preserved as a runtime no-op behind an
              // attribute-level @ts-expect-error (connect-drops-injected-keys pattern).
              // @ts-expect-error recurringContribution is connector-injected, not an accepted OwnProp
              recurringContribution={this.props.contributionInfo}
              bankAccountNumber={
                this.props.contributionSource!.accountId as string | undefined
              }
              currentContributionAmount={
                this.props.contributionInfo!.amount as number | undefined
              }
              // Added props
              bankAccount={this.props.contributionSource}
              contributionSource={this.props.contributionSource}
              accountType={this.props.contributionSource!.accountType}
            />
          )}
        {this.state.showOneTimeContributionModal && (
          <ContributionModal
            show={this.state.showOneTimeContributionModal}
            onSuccess={() => {
              this.setState({ showOneTimeContributionModal: false });
            }}
            onClose={() => {
              this.setState({ showOneTimeContributionModal: false });
            }}
            depositOnly={true}
          />
        )}
        {/* Paycheck-split removal is confirmed by the parent's own modal. */}
        {!this.props.isPaycheckSplit && (
          <RemoveSourceModal
            show={this.state.isDeleting}
            isEmployer={this.props.isEmployer}
            isLinkRequest={this.props.contributionSource!.isLinkRequest}
            onClose={() => {
              this.setState({ isDeleting: false });
            }}
            sourceId={this.props.contributionSource!.id}
          />
        )}
        <div className="source-info">
          <div className="source-title">{this._getTitle()}</div>
          <div className="source-footer">{this._getSourceFooter()}</div>
        </div>
        <div
          className={classnames("contribution-info", {
            ["no-contribution"]: isEmpty(this.props.contributionInfo),
          })}
        >
          {this._getContributionSection()}
        </div>
      </div>
    );
  }
}
export default ContributionSource;
